import { useMemo, useState } from 'react'
import { TIP_CONFIG } from './config/tip'

function TipPage({ appName, onBack }) {
  const {
    apiBaseUrl,
    authorName,
    authorTagline,
    presetAmounts,
    defaultMemo,
    minAmount,
    maxAmount,
  } = TIP_CONFIG

  const [selectedAmount, setSelectedAmount] = useState(presetAmounts[1] ?? 8.88)
  const [customAmount, setCustomAmount] = useState('')
  const [memo, setMemo] = useState(defaultMemo.replace('EmaTalking', appName))
  const [paying, setPaying] = useState(false)
  const [error, setError] = useState(null)

  const finalAmount = useMemo(() => {
    if (customAmount.trim()) {
      const parsed = parseFloat(customAmount)
      return Number.isFinite(parsed) ? parsed : null
    }
    return selectedAmount
  }, [customAmount, selectedAmount])

  const amountValid =
    finalAmount != null && finalAmount >= minAmount && finalAmount <= maxAmount

  const formattedAmount = amountValid ? finalAmount.toFixed(2) : '--'

  const openAlipayPay = async () => {
    if (!amountValid || paying) return

    setPaying(true)
    setError(null)

    try {
      const amountFen = Math.round(finalAmount * 100)
      const response = await fetch(`${apiBaseUrl.replace(/\/$/, '')}/api/tip/order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amountFen,
          memo: memo.trim() || defaultMemo.replace('EmaTalking', appName),
        }),
      })

      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(data.error || '创建打赏订单失败')
      }
      if (!data.payUrl) {
        throw new Error('支付链接为空，请稍后再试')
      }

      window.location.href = data.payUrl
    } catch (err) {
      setError(err.message || '跳转支付宝失败')
      setPaying(false)
    }
  }

  const handlePresetClick = (amount) => {
    setSelectedAmount(amount)
    setCustomAmount('')
  }

  return (
    <div className="tipPage">
      <header className="tipPage__topbar">
        <div className="container tipPage__topbarInner">
          <button type="button" className="tipPage__back" onClick={onBack}>
            ← 返回
          </button>
          <div className="tipPage__topTitle">打赏作者</div>
          <span className="tipPage__topSpacer" aria-hidden="true" />
        </div>
      </header>

      <main className="tipPage__main">
        <div className="container tipPage__content">
          <section className="tipHero">
            <div className="tipHero__avatar" aria-hidden="true">
              {authorName.slice(0, 1)}
            </div>
            <h1 className="tipHero__title">感谢你的支持</h1>
            <p className="tipHero__name">{authorName}</p>
            <p className="tipHero__desc">{authorTagline}</p>
          </section>

          <section className="tipCard">
            <h2 className="tipCard__title">选择打赏金额</h2>
            <div className="tipAmountGrid">
              {presetAmounts.map((amount) => {
                const active = !customAmount && selectedAmount === amount
                return (
                  <button
                    key={amount}
                    type="button"
                    className={`tipAmountChip${active ? ' is-active' : ''}`}
                    onClick={() => handlePresetClick(amount)}
                  >
                    <span className="tipAmountChip__currency">¥</span>
                    <span className="tipAmountChip__value">{amount}</span>
                  </button>
                )
              })}
            </div>

            <label className="tipFieldLabel" htmlFor="tip-custom-amount">
              自定义金额（元）
            </label>
            <input
              id="tip-custom-amount"
              className="tipFieldInput"
              type="number"
              min={minAmount}
              max={maxAmount}
              step="0.01"
              placeholder="输入任意金额，如 10.00"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
            />

            <label className="tipFieldLabel" htmlFor="tip-memo">
              转账备注（可选）
            </label>
            <input
              id="tip-memo"
              className="tipFieldInput"
              type="text"
              maxLength={40}
              placeholder={defaultMemo.replace('EmaTalking', appName)}
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
            />

            {!amountValid && (customAmount.trim() || finalAmount != null) && (
              <p className="tipFieldHint">
                请输入 {minAmount} ~ {maxAmount} 元之间的有效金额
              </p>
            )}
          </section>

          <section className="tipCard tipPayCard">
            <div className="tipPaySummary">
              <div>
                <p className="tipPaySummary__label">应付金额</p>
                <p className="tipPaySummary__amount">¥ {formattedAmount}</p>
              </div>
              <img src="/assets/ali.png" alt="支付宝" className="tipPaySummary__logo" />
            </div>

            {error ? <p className="tipFieldHint">{error}</p> : null}

            <button
              type="button"
              className="btn btn--solid tipPayBtn"
              disabled={!amountValid || paying}
              onClick={openAlipayPay}
            >
              {paying ? '正在跳转…' : '支付宝支付'}
            </button>

            <p className="tipPayHint">
              点击后将进入支付宝官方收银台完成支付，手机端会自动唤起支付宝 App。
            </p>
          </section>

          <section className="tipSteps">
            <div className="tipStep">
              <span className="tipStep__num">1</span>
              <span>选择或输入打赏金额</span>
            </div>
            <div className="tipStep">
              <span className="tipStep__num">2</span>
              <span>点击「支付宝支付」进入收银台</span>
            </div>
            <div className="tipStep">
              <span className="tipStep__num">3</span>
              <span>在支付宝中确认并完成支付</span>
            </div>
          </section>

          <p className="tipThanks">每一份支持都会化作继续更新的动力，谢谢你 ❤️</p>
        </div>
      </main>
    </div>
  )
}

export default TipPage
